import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { resizePhoto } from "../lib/image.js";
import { localNowStr, localToday, localDatePlus, fmt12, fmtDay, nextDateForTime } from "../lib/dates.js";
import { Segmented, inputCls } from "../components/ui.jsx";
import { PlatformBadge } from "../components/SocialIcons.jsx";

const PLATFORM_META = {
  linkedin: { label: "LinkedIn", limit: 3000, warn: 1300 },
  facebook: { label: "Facebook", limit: 63206, warn: 600 },
  instagram: { label: "Instagram", limit: 2200, warn: 300 },
  x: { label: "X", limit: 280, warn: 260 },
};
const PLATFORM_ORDER = Object.keys(PLATFORM_META); // LinkedIn first — primary channel
// New drafts are LinkedIn + Facebook only; the full META/ORDER lists stay so
// older posts that carried X or Instagram variants still render.
const ACTIVE_PLATFORMS = ["linkedin", "facebook"];

// One fetch shape for the {platform: "HH:MM"} map, shared by Queue + detail.
async function loadBestTimes() {
  const d = await api("/api/social/best-times");
  const m = {};
  (d.best_times || []).forEach((t) => (m[t.platform] = t.suggested_time));
  return m;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// Older drafts (and the occasional model slip) end the copy with the hashtags
// already — never append the field again when the copy contains those tags.
const pasteTextFor = (copy, hashtags) => {
  const tags = (hashtags || "").trim();
  if (!tags || copy.includes(tags)) return copy;
  return `${copy}\n\n${tags}`;
};

// One share path for the post page + calendar: the system share sheet with the
// image and caption together. Most apps take the file and DROP the share text
// (LinkedIn and Instagram both do), so the caption is also put on the
// clipboard up front — the paste that follows in the app is the caption, not
// whatever was copied earlier (e.g. the image from ChatGPT). Returns a note
// to show under the button.
async function shareImageAndText(imageUrl, pasteText) {
  const copied = await copyText(pasteText);
  const captionNote = copied ? "Caption copied — paste it into the post if the app didn't fill it in." : "";
  try {
    const res = await fetch(imageUrl, { credentials: "include" });
    const blob = await res.blob();
    const file = new File([blob], "post.jpg", { type: blob.type || "image/jpeg" });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], text: pasteText });
      return captionNote;
    }
    return copied
      ? "Sharing isn't supported in this browser — caption copied; save the image manually."
      : "Sharing isn't supported in this browser.";
  } catch (e) {
    if (e.name === "AbortError") return captionNote;
    return `Share didn't complete — ${copied ? "the caption is copied; " : ""}attach the image manually.`;
  }
}

function CopyButton({ text, label = "📋 Copy" }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        if (await copyText(text)) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
      }}
      className="rounded-xl bg-navy-700 px-4 py-2.5 text-sm font-semibold text-white"
    >
      {copied ? "Copied ✓" : label}
    </button>
  );
}

/* ---------------- Best-times settings ---------------- */
function BestTimesCard({ times, onSaved }) {
  const [editing, setEditing] = useState(null); // platform | null
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);

  async function save(platform) {
    await api("/api/social/best-times", { method: "PUT", body: { platform, suggested_time: draft } });
    setEditing(null);
    onSaved();
  }

  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <p className="text-sm font-semibold text-slate-700">⚙️ Best posting times</p>
        <span className="text-slate-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {ACTIVE_PLATFORMS.map((p) => {
            const t = times[p];
            const meta = PLATFORM_META[p];
            return editing === p ? (
              <div key={p} className="flex items-center gap-2">
                <span className="flex w-28 shrink-0 items-center gap-1.5 text-sm text-slate-600">
                  <PlatformBadge platform={p} /> {meta.label}
                </span>
                <input type="time" value={draft} onChange={(e) => setDraft(e.target.value)} className={inputCls} />
                <button onClick={() => save(p)} className="shrink-0 rounded-lg bg-navy-700 px-3 py-2 text-xs font-semibold text-white">
                  Save
                </button>
              </div>
            ) : (
              <div key={p} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <PlatformBadge platform={p} /> {meta.label}
                </span>
                <button
                  onClick={() => { setEditing(p); setDraft(t || "09:00"); }}
                  className="font-semibold text-navy-700"
                >
                  {t ? fmt12(t) : "not set"} · edit
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ---------------- Queue ---------------- */
function Queue() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [bestTimes, setBestTimes] = useState({});
  const [idea, setIdea] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const loadTimes = () => loadBestTimes().then(setBestTimes).catch(() => {});
  const load = () => api("/api/social/posts").then((d) => setPosts(d.posts)).catch(() => setPosts([]));
  useEffect(() => {
    load();
    loadTimes();
  }, []);

  async function generate(e) {
    e.preventDefault();
    if (!idea.trim()) return;
    setGenerating(true);
    setError("");
    try {
      const res = await api("/api/social/generate", { method: "POST", body: { idea } });
      setIdea("");
      navigate(`/social/${res.id}`);
    } catch (err) {
      setError(err.message || "Couldn't generate drafts");
      setGenerating(false);
    }
  }

  const now = localNowStr();
  const dueNow = (posts || []).filter((p) => p.variants.some((v) => v.scheduled_at && v.scheduled_at <= now && !v.posted_at));
  // The queue is the to-do list: drafts only. Scheduled posts live on the
  // calendar, posted ones in its history — done work doesn't linger here.
  const drafts = (posts || []).filter((p) => p.status === "draft");
  const handledCount = (posts || []).length - drafts.length;

  return (
    <>
      {dueNow.length > 0 && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">📣 Ready to post</p>
          <div className="mt-2 space-y-1.5">
            {dueNow.map((p) => (
              <button key={p.id} onClick={() => navigate(`/social/${p.id}`)} className="block w-full truncate text-left text-sm text-amber-800">
                • {p.idea} →
              </button>
            ))}
          </div>
        </section>
      )}

      <form onSubmit={generate} className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-slate-700">✨ New idea</p>
        <textarea
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          rows={3}
          placeholder="e.g. We just wrapped a workshop with a local nonprofit and they built their first Claude Artifact"
          className={inputCls}
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button disabled={generating || !idea.trim()} className="w-full rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {generating ? "Drafting…" : "Generate drafts"}
        </button>
      </form>

      <BestTimesCard times={bestTimes} onSaved={loadTimes} />

      <section>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Queue</p>
        {posts === null && <p className="text-center text-sm text-slate-400">Loading…</p>}
        {posts !== null && drafts.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <p className="text-4xl">📣</p>
            <p className="mt-3 font-medium text-slate-700">{handledCount > 0 ? "Queue clear" : "No posts yet"}</p>
            <p className="mt-1 text-sm text-slate-500">
              {handledCount > 0
                ? "Everything is scheduled or posted — it's all on the calendar."
                : "Drop an idea above and get LinkedIn and Facebook drafts back."}
            </p>
          </div>
        )}
        {drafts.length > 0 && (
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {drafts.map((p) => (
              <button key={p.id} onClick={() => navigate(`/social/${p.id}`)} className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{p.idea}</p>
                  <div className="mt-1 flex gap-1.5">
                    {p.variants.map((v) => (
                      <PlatformBadge key={v.id} platform={v.platform} className="h-4 w-4" />
                    ))}
                  </div>
                </div>
                <span className="ml-2 shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
                  draft
                </span>
              </button>
            ))}
          </div>
        )}
        {handledCount > 0 && drafts.length > 0 && (
          <p className="mt-2 text-center text-xs text-slate-400">
            {handledCount} more scheduled or posted — see the Calendar tab.
          </p>
        )}
      </section>
    </>
  );
}

/* ---------------- Post image (made in ChatGPT, attached here) ---------------- */
function PostImageCard({ post, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const uploadRef = useRef(null);

  async function upload(file) {
    if (!file || !file.type?.startsWith("image/")) return;
    setBusy(true);
    try {
      const blob = await resizePhoto(file, 2048, 0.9);
      const fd = new FormData();
      fd.append("image", blob, "post.jpg");
      const res = await fetch(`/api/social/posts/${post.id}/image`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error("Upload failed");
      setVersion((v) => v + 1);
      onChanged();
    } catch {
      alert("Couldn't attach the image — try again.");
    } finally {
      setBusy(false);
    }
  }
  uploadRef.current = upload;

  function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    upload(file);
  }

  // Desktop: ⌘V anywhere on the post page attaches straight from the clipboard
  // (ChatGPT: right-click the image → Copy image). Text pastes carry no files
  // and pass through to whatever input has focus.
  useEffect(() => {
    function onPaste(e) {
      const file = [...(e.clipboardData?.files || [])].find((f) => f.type.startsWith("image/"));
      if (file) {
        e.preventDefault();
        uploadRef.current(file);
      }
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  return (
    <section
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        upload(e.dataTransfer.files?.[0]);
      }}
      className={`rounded-2xl bg-white p-4 shadow-sm ${dragOver ? "ring-2 ring-navy-600/60" : ""}`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">🖼️ Post image</p>
      {post.image_key ? (
        <>
          <img
            src={`/api/social/posts/${post.id}/image?v=${version}`}
            alt="Post"
            className="mt-2 w-full rounded-xl"
          />
          <div className="mt-2 flex gap-3">
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="text-xs font-semibold text-navy-700">
              {busy ? "Uploading…" : "Replace"}
            </button>
            <button
              onClick={async () => {
                await api(`/api/social/posts/${post.id}/image`, { method: "DELETE" });
                onChanged();
              }}
              className="text-xs font-semibold text-slate-400"
            >
              Remove
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-1 text-xs text-slate-500">
            Generate the image in ChatGPT with the prompt above, then attach it here — every platform's Share button
            will carry it. Phone: save to Photos, tap Attach. Mac: right-click the image, Copy image, press ⌘V here
            (or drag the file onto this card).
          </p>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="mt-2 w-full rounded-xl border-2 border-dashed border-navy-600/40 bg-navy-700/5 py-3 text-sm font-semibold text-navy-700 disabled:opacity-60"
          >
            {busy ? "Uploading…" : "📎 Attach image"}
          </button>
        </>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
    </section>
  );
}

/* ---------------- Variant editor ---------------- */
function VariantCard({ variant, bestTime, imageUrl, onSaved }) {
  const meta = PLATFORM_META[variant.platform] || { label: variant.platform, limit: 9999, warn: 9999 };
  const [copy, setCopy] = useState(variant.copy);
  const [date, setDate] = useState(variant.scheduled_at ? variant.scheduled_at.slice(0, 10) : "");
  const [time, setTime] = useState(variant.scheduled_at ? variant.scheduled_at.slice(11, 16) : "");
  const [busy, setBusy] = useState(false);

  // The schedule can change outside this card ("Schedule all" on the post) —
  // re-sync the inputs whenever the server value moves.
  useEffect(() => {
    setDate(variant.scheduled_at ? variant.scheduled_at.slice(0, 10) : "");
    setTime(variant.scheduled_at ? variant.scheduled_at.slice(11, 16) : "");
  }, [variant.scheduled_at]);

  // Hashtags live inside the copy now. Older drafts still carry a stored
  // hashtags field — the paste text appends it so nothing they had is lost.
  const pasteText = pasteTextFor(copy, variant.hashtags || "");
  const total = pasteText.length;
  const over = total > meta.limit;
  const warn = !over && total > meta.warn;

  async function saveContent() {
    if (copy === variant.copy) return;
    await api(`/api/social/variants/${variant.id}`, { method: "PUT", body: { copy } });
    onSaved();
  }

  // Called on blur / explicit taps — never per keystroke of the date inputs.
  async function applySchedule(d, t) {
    await api(`/api/social/variants/${variant.id}`, { method: "PUT", body: { scheduled_at: d && t ? `${d} ${t}` : null } });
    onSaved();
  }

  // One tap onto the calendar at this platform's recommended slot — today if
  // the slot hasn't passed yet on the business clock, otherwise tomorrow.
  function useBestTime() {
    const t = bestTime || "09:00";
    const d = nextDateForTime(t);
    setDate(d);
    setTime(t);
    applySchedule(d, t);
  }

  async function togglePosted() {
    setBusy(true);
    try {
      await api(`/api/social/variants/${variant.id}`, { method: "PUT", body: { posted: !variant.posted_at } });
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  const now = localNowStr();
  const isDue = variant.scheduled_at && variant.scheduled_at <= now && !variant.posted_at;
  const isPosted = !!variant.posted_at;

  const [shareNote, setShareNote] = useState("");
  async function shareBoth() {
    setShareNote(await shareImageAndText(imageUrl, pasteText));
  }

  return (
    <section className={`rounded-2xl p-4 shadow-sm ${isDue ? "border-2 border-amber-300 bg-amber-50" : "bg-white"}`}>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <PlatformBadge platform={variant.platform} className="h-6 w-6" /> {meta.label}
        </p>
        {isPosted && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">Posted</span>}
        {isDue && !isPosted && <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900">Due now</span>}
      </div>

      <textarea value={copy} onChange={(e) => setCopy(e.target.value)} onBlur={saveContent} rows={5} className={`${inputCls} mt-2`} />
      <p className={`mt-1 text-xs ${over ? "font-semibold text-red-600" : warn ? "text-amber-600" : "text-slate-400"}`}>
        {total} / {meta.limit} characters{over ? " — over the platform limit" : ""}
      </p>

      <div className="mt-3 flex gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          onBlur={() => date && time && applySchedule(date, time)}
          className={inputCls}
        />
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          onBlur={() => date && time && applySchedule(date, time)}
          className={inputCls}
        />
      </div>
      {!date && !isPosted && (
        <button
          onClick={useBestTime}
          className="mt-2 w-full rounded-xl border border-navy-600/30 bg-navy-700/5 py-2.5 text-sm font-semibold text-navy-700"
        >
          📅 Schedule{bestTime ? ` for best time (${fmt12(bestTime)})` : ""}
        </button>
      )}
      {date && (
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-slate-500">
            📅 On the calendar for {fmtDay(date)}
            {time ? ` · ${fmt12(time)}` : ""}
          </span>
          <button
            onClick={() => {
              setDate("");
              setTime("");
              applySchedule(null, null);
            }}
            className="font-semibold text-slate-400"
          >
            Clear
          </button>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        {imageUrl && (
          <button onClick={shareBoth} className="flex-1 rounded-xl bg-navy-700 py-2.5 text-sm font-semibold text-white">
            📤 Share image + text
          </button>
        )}
        <div className="flex-1">
          <CopyButton text={pasteText} label="📋 Copy text" />
        </div>
        <button
          onClick={togglePosted}
          disabled={busy}
          className={`flex-1 rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 ${
            isPosted ? "border border-slate-300 text-slate-500" : "bg-emerald-600 text-white"
          }`}
        >
          {isPosted ? "Undo posted" : "✓ Mark posted"}
        </button>
      </div>
      {shareNote && <p className="mt-1 text-xs text-slate-500">{shareNote}</p>}
      {imageUrl && !shareNote && (
        <p className="mt-1 text-xs text-slate-400">
          Share sends the image to the app; the caption lands on your clipboard — paste it into the post.
        </p>
      )}
    </section>
  );
}

/* ---------------- Post detail ---------------- */
export function SocialPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [bestTimes, setBestTimes] = useState({});
  const [error, setError] = useState("");
  const [scheduling, setScheduling] = useState(false);

  const loadPost = () => api(`/api/social/posts/${id}`).then(setData).catch(() => setError("Post not found."));
  useEffect(() => {
    loadPost();
    // Best times can't change from this page — fetch once, not on every save.
    loadBestTimes().then(setBestTimes).catch(() => {});
  }, [id]);

  async function remove() {
    if (!window.confirm("Delete this post idea and all its drafts?")) return;
    await api(`/api/social/posts/${id}`, { method: "DELETE" });
    navigate("/social");
  }

  if (error)
    return (
      <Shell title="Post" back>
        <p className="text-sm text-red-600">{error}</p>
      </Shell>
    );
  if (!data)
    return (
      <Shell title="Post" back>
        <p className="text-center text-sm text-slate-400">Loading…</p>
      </Shell>
    );

  const { post, variants } = data;
  const sorted = [...variants].sort((a, b) => PLATFORM_ORDER.indexOf(a.platform) - PLATFORM_ORDER.indexOf(b.platform));

  // One tap puts every unscheduled draft on the calendar, each at its own
  // platform's recommended slot.
  const unscheduled = sorted.filter((v) => !v.scheduled_at && !v.posted_at);
  async function scheduleAll() {
    setScheduling(true);
    try {
      for (const v of unscheduled) {
        const t = bestTimes[v.platform] || "09:00";
        await api(`/api/social/variants/${v.id}`, { method: "PUT", body: { scheduled_at: `${nextDateForTime(t)} ${t}` } });
      }
      loadPost();
    } finally {
      setScheduling(false);
    }
  }

  return (
    <Shell title="Post" back>
      <section className="rounded-2xl bg-white p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Idea</p>
        <p className="mt-1 text-sm text-slate-700">{post.idea}</p>
      </section>

      {post.image_prompt && (
        <section className="rounded-2xl bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">🎨 Image prompt (paste into ChatGPT)</p>
          <p className="mt-1 text-sm text-slate-700">{post.image_prompt}</p>
          <div className="mt-2">
            <CopyButton text={post.image_prompt} label="📋 Copy image prompt" />
          </div>
        </section>
      )}

      <PostImageCard post={post} onChanged={loadPost} />

      {unscheduled.length > 1 && (
        <button
          onClick={scheduleAll}
          disabled={scheduling}
          className="w-full rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {scheduling ? "Scheduling…" : `📅 Schedule all ${unscheduled.length} at best times`}
        </button>
      )}

      {sorted.map((v) => (
        <VariantCard
          key={v.id}
          variant={v}
          bestTime={bestTimes[v.platform]}
          imageUrl={post.image_key ? `/api/social/posts/${post.id}/image` : null}
          onSaved={loadPost}
        />
      ))}

      <button onClick={remove} className="w-full rounded-xl border border-red-200 py-3 text-sm font-semibold text-red-600">
        Delete this idea
      </button>
    </Shell>
  );
}

/* ---------------- Calendar ---------------- */
function CalendarEntry({ v, onChanged }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = PLATFORM_META[v.platform] || { label: v.platform };
  const isDue = v.scheduled_at <= localNowStr();
  const pasteText = pasteTextFor(v.copy, v.hashtags || "");
  const imageUrl = v.image_key ? `/api/social/posts/${v.post_id}/image` : null;

  async function markPosted() {
    setBusy(true);
    try {
      await api(`/api/social/variants/${v.id}`, { method: "PUT", body: { posted: true } });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={isDue ? "bg-amber-50" : ""}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2.5 px-4 py-3 text-left">
        <PlatformBadge platform={v.platform} className="h-6 w-6" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-navy-700">
            {fmt12(v.scheduled_at.slice(11, 16))} · {meta.label}
          </span>
          <span className="block truncate text-sm text-slate-700">{v.idea}</span>
        </span>
        {isDue && (
          <span className="shrink-0 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
            Due
          </span>
        )}
      </button>
      {open && (
        <div className="space-y-2 px-4 pb-3">
          {imageUrl && <img src={imageUrl} alt="" className="max-h-40 rounded-xl" />}
          <p className="whitespace-pre-wrap text-sm text-slate-600">{pasteText}</p>
          <div className="flex gap-2">
            {imageUrl ? (
              <button
                onClick={async () => setNote(await shareImageAndText(imageUrl, pasteText))}
                className="flex-1 rounded-xl bg-navy-700 py-2.5 text-sm font-semibold text-white"
              >
                📤 Share image + text
              </button>
            ) : (
              <div className="flex-1">
                <CopyButton text={pasteText} label="📋 Copy text" />
              </div>
            )}
            <button
              onClick={markPosted}
              disabled={busy}
              className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              ✓ Mark posted
            </button>
          </div>
          {note && <p className="text-xs text-slate-500">{note}</p>}
          {imageUrl && !note && (
            <p className="text-xs text-slate-400">
              Share sends the image to the app; the caption lands on your clipboard — paste it into the post.
            </p>
          )}
          <div className="flex items-center gap-4">
            {imageUrl && <CopyButton text={pasteText} label="📋 Copy text" />}
            <button onClick={() => navigate(`/social/${v.post_id}`)} className="text-xs font-semibold text-navy-700">
              Edit post →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SocialCalendar() {
  const [data, setData] = useState(null);
  const [showPosted, setShowPosted] = useState(false);
  const load = () => api("/api/social/calendar").then(setData).catch(() => setData({ upcoming: [], posted: [] }));
  useEffect(() => {
    load();
  }, []);

  if (!data) return <p className="text-center text-sm text-slate-400">Loading…</p>;

  // Group by day, preserving schedule order from the API.
  const days = [];
  for (const v of data.upcoming) {
    const d = v.scheduled_at.slice(0, 10);
    if (!days.length || days[days.length - 1].date !== d) days.push({ date: d, items: [] });
    days[days.length - 1].items.push(v);
  }
  const today = localToday();
  const tomorrow = localDatePlus(1);
  const label = (d) => (d === today ? "Today" : d === tomorrow ? "Tomorrow" : fmtDay(d));

  return (
    <>
      {days.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-4xl">📅</p>
          <p className="mt-3 font-medium text-slate-700">Nothing scheduled</p>
          <p className="mt-1 text-sm text-slate-500">Open a post and tap Schedule to put it on the calendar.</p>
        </div>
      )}
      {days.map((day) => (
        <section key={day.date}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{label(day.date)}</p>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm">
            {day.items.map((v) => (
              <CalendarEntry key={v.id} v={v} onChanged={load} />
            ))}
          </div>
        </section>
      ))}
      {data.posted.length > 0 && (
        <section>
          <button
            onClick={() => setShowPosted((s) => !s)}
            className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400"
          >
            Posted ({data.posted.length}) {showPosted ? "▲" : "▼"}
          </button>
          {showPosted && (
            <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm">
              {data.posted.map((v) => (
                <div key={v.id} className="flex items-center gap-2.5 px-4 py-3">
                  <PlatformBadge platform={v.platform} />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-500">{v.idea}</span>
                  <span className="shrink-0 text-xs font-semibold text-emerald-600">✓ {fmtDay(v.posted_at.slice(0, 10))}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  );
}

const SOCIAL_TABS = [
  { value: "queue", label: "Compose" },
  { value: "calendar", label: "Calendar" },
];

export default function Social() {
  const [tab, setTab] = useState("queue");
  return (
    <Shell title="Social">
      <Segmented options={SOCIAL_TABS} value={tab} onChange={setTab} />
      {tab === "queue" ? <Queue /> : <SocialCalendar />}
    </Shell>
  );
}
